export interface AirbnbQualityStats {
  entity_type: 'listing' | 'user'
  entity_id: string
  monthly_quality_stats: AirbnbMonthlyQualityStats[]
}

export interface AirbnbMonthlyQualityStats {
  month: string // 2025-06-01
  trips_cs_report_high_severity: number
  overall_issue_trip_rate: number
  cancellations_by_host?: number
  host_cancellation_rate: number
}

export interface AirbnbQualityStandards {
  score: AirbnbScore
  state: string // can be one of GOOD, WARN, PROBATION, SUSPEND, ADDITIONAL_WARN, PENDING_REMOVAL, EDUCATE, and REMOVED
  metadata?: any
}

export function getStatusColor(state: string) {
  // GOOD, WARN, PROBATION, SUSPEND, ADDITIONAL_WARN, PENDING_REMOVAL, EDUCATE, and REMOVED
  switch (state) {
    case 'GOOD':
      return 'success'
    case 'WARN':
    case 'ADDITIONAL_WARN':
      return 'warning'
    case 'PROBATION':
    case 'PENDING_REMOVAL':
      return 'warning'
    case 'SUSPEND':
    case 'REMOVED':
      return 'error'
    case 'EDUCATE':
      return 'info'
    default:
      return 'default'
  }
}

const qualityTooltips = {
  good: $localize`No quality issue reported`,
  educate: $localize`A quality issue has been reported. Host should review guidelines and educational materials.`,
  warn: $localize`A few quality issues have been reported. Host should review our guidelines again`,
  probation: $localize`A few quality issues have been reported. Listing is at risk of suspension soon. Host should review our guidelines again`,
  additional_warn: $localize`Too many quality issues have been reported. The listing is at risk for removal`,
  pending_removal: $localize`Too many quality issues have been reported. The listing is marked for removal after 30 days.`,
  suspended: $localize`Too many quality issues have been reported. The listing has been suspended until hosts reviews Airbnb guidelines again`,
  removed: $localize`Too many quality issues have been reported. The listing has been removed from Airbnb.`,
}

export function getAirbnbListingStatusTooltip(status: string): string {
  const key = status?.toLowerCase()?.replace(/ /g, '_')
  return qualityTooltips[key] || ''
}

export interface AirbnbScore {
  is_guest_favorite: boolean
  quality_percentage: number
}

export interface AirbnbReservationIssue {
  confirmation_code: string
  cancellation?: string // CANCELLATION_BEYOND_30D CANCELLATION_BETWEEN_48H_30D CANCELLATION_WITHIN_48H
  cs_violations: AirbnbCSViolaltionItem[] // Quality details related to Airbnb policy violations.
  review_issues?: AirbnbReviewIssues
}

export interface AirbnbCSViolaltionItem {
  category: string
  tag: string
}

export interface AirbnbNegativeCategoryTag {
  category: string
  tag: string
}

export interface AirbnbReviewIssues {
  low_overall_rating: number
  low_category_ratings: AirbnbLowCategoryRating[]
  review_id: string
  negative_category_tags: AirbnbNegativeCategoryTag[]
}

export interface AirbnbLowCategoryRating {
  rating: number
  category: string
}

export interface AirbnbListingQualityItem {
  account: string
  display_exact_location_to_guest: boolean
  room_type_category: string
  directions: string
  lng: number
  property_type_category: string
  has_active_disaster: boolean
  lat: number
  categories: string[]
  synchronization_category: string
  bedrooms: number
  property_type_group: string
  property_external_id?: string
  country_code: string
  bathrooms: number
  person_capacity: number
  beds: number
  state?: string
  tier: string
  has_availability: boolean
  apt?: string
  total_inventory_count: number
  zipcode: string
  city: string

  propertyId: string
  id: string
  name: string
  quality_standards: AirbnbQualityStandards
  reservation_issues: AirbnbReservationIssue[]
}

export interface AirbnbRatingStats {
  category_stats?: AirbnbCategoryRatingStat[] // for listing
  entity_type: 'listing' | 'user'
  entity_id: string
  rating_last_year: number
  count: number
  rating: number
}

export interface AirbnbCategoryRatingStat {
  count: number
  rating: number
  category_tag_count_map: Record<string, number>
  category: string // communication | cleanliness | accuracy | location | check-in | value
}
